import type {
  AnnotationStorage,
  FieldObjects,
  Item,
  ClientPortalQuestionnaireSubmissionSchema,
  SubmitFormFieldObjectPayloadObject,
  ClientPortalCaseSchema,
  Page
} from '@/types'
import type { ClassValue } from 'clsx'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const pick = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  const result: Pick<T, K> = {} as Pick<T, K>
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

export function generateFieldId(field: Item, index: number): string {
  return `field_${index}_${field.label.replace(/\s+/g, '_').toLowerCase()}`
}

export function getFormConfig(
  submissions: ClientPortalQuestionnaireSubmissionSchema[],
  id?: string
): {
  items: (Item & { id: string })[]
  activeFieldId: string
  title: string
  totalQuestionCount: number
} | null {
  if (!submissions || submissions.length === 0) {
    return null
  }

  const submission = id ? submissions.find((sub) => sub.shortId === id) : submissions[0]
  if (!submission || !submission.schema_ || submission.schema_.length === 0) {
    return null
  }

  const outputItems: (Item & { id: string })[] = []
  let lastFieldWithValueId = ''
  let totalItemsCount = 0

  submission.schema_.forEach((page: Page) => {
    page.items.forEach((item: Item) => {
      const itemWithId = {
        ...item,
        id: generateFieldId(item, totalItemsCount)
      }
      outputItems.push(itemWithId)

      // Update lastFieldWithValueId if the current field has a value
      if (item.value !== undefined && item.value !== '') {
        lastFieldWithValueId = itemWithId.id
      }

      totalItemsCount++
    })
  })

  // Set activeFieldId to the last field with a value, or the first field if no fields have values
  const activeFieldId = lastFieldWithValueId || (outputItems.length > 0 ? outputItems[0].id : '')

  return {
    items: outputItems,
    activeFieldId: activeFieldId,
    title: submission.title,
    totalQuestionCount: submission.questionCount
  }
}

export function createInstructions(_firstName: string, _lastName: string) {
  return `Bitte beantworten Sie die folgenden Fragen.
          <p class="text-md opacity-70"> Benutzen Sie die
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="inline opacity-85 text-lg" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24">
            <path fill="currentColor" d="m4.431 12.822l13 9A1 1 0 0 0 19 21V3a1 1 0 0 0-1.569-.823l-13 9a1.003 1.003 0 0 0 0 1.645z"/>
          </svg> Taste um zurückzuspringen und die
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="inline opacity-85 text-lg" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24">
            <path fill="currentColor" d="M5.536 21.886a1.004 1.004 0 0 0 1.033-.064l13-9a1 1 0 0 0 0-1.644l-13-9A1 1 0 0 0 5 3v18a1 1 0 0 0 .536.886z"/>
          </svg> Taste um fortzufahren.</p>`
}

export function waitForElement(selector: string): {
  promise: Promise<Element>
  cancel: () => void
} {
  let observer: MutationObserver

  const promise = new Promise<Element>((resolve) => {
    const el = document.querySelector(selector)
    if (el) {
      resolve(el)
      return
    }

    observer = new MutationObserver(() => {
      const el = document.querySelector(selector)
      if (el) {
        cleanup()
        resolve(el)
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  })

  const cleanup = () => {
    if (observer) {
      observer.disconnect()
    }
  }

  return { promise, cancel: cleanup }
}

export type Entries<T> = {
  [K in keyof T]: [K, T[K]]
}[keyof T][]

export const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>

export async function annotationMapper(
  fieldObjects: FieldObjects,
  annotationStorage: AnnotationStorage | undefined
): Promise<Array<SubmitFormFieldObjectPayloadObject>> {
  return Object.values(fieldObjects)
    .flat()
    .map((obj) => {
      // Ensure that the value is always a string
      let value: string
      if (annotationStorage && annotationStorage[obj.id]) {
        value = String(annotationStorage[obj.id].value)
      } else {
        value = obj.value !== null && obj.value !== undefined ? String(obj.value) : ''
      }

      return {
        id: obj.id,
        name: obj.name,
        page: obj.page,
        type: obj.type,
        defaultValue: obj.defaultValue,
        exportValues: obj.exportValues,
        value: value
      }
    })
}

export function basename(path: string) {
  if (path === undefined) {
    return ''
  }
  return path.split('/').reverse()[0]
}

export function base64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

// https://blog.logrocket.com/programmatically-downloading-files-browser/
export function downloadBlob(blob: Blob, filename: string) {
  // Create an object URL for the blob object
  const url = URL.createObjectURL(blob)

  // Create a new anchor element
  const a = document.createElement('a')

  // Set the href and download attributes for the anchor element
  // You can optionally set other attributes like `title`, etc
  // Especially, if the anchor element will be attached to the DOM
  a.href = url
  a.download = filename || 'download'

  // Click handler that releases the object URL after the element has been clicked
  // This is required for one-off downloads of the blob content
  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url)
      removeEventListener('click', clickHandler)
    }, 150)
  }

  // Add the click event listener on the anchor element
  // Comment out this line if you don't want a one-off download of the blob content
  a.addEventListener('click', clickHandler, false)

  // Programmatically trigger a click on the anchor element
  // Useful if you want the download to happen automatically
  // Without attaching the anchor element to the DOM
  // Comment out this line if you don't want an automatic download of the blob content
  a.click()

  // Return the anchor element
  // Useful if you want a reference to the element
  // in order to attach it to the DOM or use it in some other way
  return a
}

export function downloadDocument(url: string, filename: string = basename(url)) {
  fetch(url)
    .then((response) => {
      return response.arrayBuffer()
    })
    .then((data) => {
      const blob = new Blob([data], { type: 'application/pdf' })
      downloadBlob(blob, filename)
    })
    .catch(console.error)
}

export const c11 =
  'color: #f33, border-radius: 5px; padding: 5px; background: #212121; font-size: 12px; font-weight: bold; border: 2px solid #f33'

export function getBadgeDetailsForSubmission(item: ClientPortalQuestionnaireSubmissionSchema) {
  if (item.state === 'CLOSED') {
    return {
      text: 'Abgeschlossen',
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-700',
      hoverBgColor: 'hover:bg-teal-200'
    }
  } else if (item.state === 'RECEIVED' || item.state === 'OPEN') {
    return {
      text: 'Ausstehend',
      bgColor: 'bg-red-100',
      textColor: 'text-red-700',
      hoverBgColor: 'hover:bg-red-200'
    }
  }
  return { text: '', bgColor: '', textColor: '', hoverBgColor: '' } // Default case
}

export function extractAllSubmissions(
  cases: Array<ClientPortalCaseSchema>
): Array<ClientPortalQuestionnaireSubmissionSchema> {
  return cases.map((caseItem) => caseItem.submissions).flat()
}

export const getColorForString = (value: string): string => {
  /**
   * Returns a color based on the sum of the character codes of the input string. The color is chosen from a
   * predefined list of colors. Allows for consistent color assignment based on the input string.
   *
   * This function is useful for assigning colors to elements based on their names or other string values.
   *
   * For example, when displaying user avatars or tags. To calculate the background color for an avatar based on the
   * organisations name, you can use this function as follows:
   *
   * const avatarColor = computed(() => {
   *   return `bg-${getColorForString(props.org.name)}-600`
   * })
   *
   * @param {string} value - The input string.
   */
  const colors = ['red', 'yellow', 'blue', 'green', 'indigo', 'purple', 'pink']

  value = value.toLowerCase()
  const sum = [...value].map((character) => character.charCodeAt(0)).reduce((a, b) => a + b, 0)

  const colorIndex = sum % colors.length
  return colors[colorIndex]
}

export const getInitials = (name: string): string => {
  /**
   * Returns the initials of a name as a string. The initials are the first letter of each word in the name.
   * A maximum of two initials are returned to account for names with multiple words.
   *
   * @param {string} name - The name to extract the initials from.
   */
  const words: string[] = name.trim().split(/\s+/)
  let initials: string = ''

  if (words.length === 1) {
    initials = words[0].slice(0, 2).toUpperCase()
  } else {
    for (let i = 0; i < words.length && initials.length < 2; i++) {
      initials += words[i][0].toUpperCase()
    }
  }

  return initials
}

export function generateId(field: Item, index: number): string {
  // Use a combination of index and label to create a unique, deterministic ID
  // This is used for our Questionnaires
  return `field_${index}_${field.label.replace(/\s+/g, '_').toLowerCase()}`
}
